import getenv from 'getenv';
import ReCAPTCHA from 'react-google-recaptcha';
import type { HTTPError } from 'ky';
import { Field, Form, Formik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import Button from '@tmh-website/ui/Button';
import FormSection from '@tmh-website/ui/FormSection';
import Input from '@tmh-website/ui/Input';
import Select from '@tmh-website/ui/Select';
import Textarea from '@tmh-website/ui/Textarea';
import type { SelectProps } from '@tmh-website/ui/Select';
import { Breakpoint } from '@tmh-website/shared/const';
import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import { getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import { StyledFormElements } from '@tmh-website/ui/FormElements';

import * as StyledStaticForms from '../StaticForms.styles';

import {
  INITIAL_VALUES as initialValues,
  PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP,
  validationSchema,
} from './ProductSupportFormUSA_EN.config';

import {
  ProductSupportFormPayloadUSA_EN,
  ProductSupportFormPropsUSA_EN,
  ProductSupportFormValuesUSA_EN,
} from './ProductSupportFormUSA_EN.contracts';
import { mapValuesToPayload } from './ProductSupportFormUSA_EN.helpers';

const ProductSupportFormUSAEN = (props: ProductSupportFormPropsUSA_EN) => {
  const { formElementThemeColor, buttonThemeColor, buttonHoverThemeColor } = props;

  const { t } = useTranslation();
  const { locale } = useRouter();

  /**
   * Resolves the options/values for the `<Select>` component that handles the field of a given name.
   *
   * @param fieldName - The name of the field which options we want to resolve.
   * @param translationKey - The i18n translation key that should be used to resolve the applicable values.
   *
   * @returns - Array of options for the `<Select>` input/component.
   */
  const resolveSelectOptions = (
    fieldName: keyof ProductSupportFormValuesUSA_EN,
    translationKey: string
  ): SelectProps['options'] => {
    const options: SelectProps['options'] = t(translationKey, { returnObjects: true }) as SelectProps['options'];

    if (!Array.isArray(options) || options.length === 0) {
      throw new Error(`Failed to resolve the options array for the '${fieldName}' field!`);
    }

    return options;
  };

  const {
    mutateAsync: submitMutation,
    isSuccess,
    isError,
  } = useMutation<void, HTTPError, ProductSupportFormValuesUSA_EN>(async values => {
    const payload: ProductSupportFormPayloadUSA_EN = mapValuesToPayload(values, props.formParameters, locale, t);
    await cmsWebClient.post('static-form/submit', { json: payload }).text();
  });

  return (
    <Formik<ProductSupportFormValuesUSA_EN>
      {...{ initialValues, validationSchema }}
      onSubmit={values => submitMutation(values)}>
      {({ isSubmitting, setFieldValue, errors, touched }) => (
        <Form>
          <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
            {/* First name */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.firstName)}
              name='firstName'
              autoComplete='given-name'
              required
            />
            {/* END | First name */}

            {/* Last name */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.lastName)}
              name='lastName'
              autoComplete='family-name'
              required
            />
            {/* END | Last name */}

            {/* Company */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.company)}
              name='company'
              autoComplete='organization'
              required
            />
            {/* END | Company */}

            {/* Phone */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.phone)}
              name='phone'
              autoComplete='phone'
              type='tel'
              required
            />
            {/* END | Phone */}
          </FormSection>

          <FormSection numberOfColumns={1}>
            {/* E-mail */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.email)}
              name='email'
              autoComplete='email'
              type='email'
              required
            />
            {/* END | E-mail */}

            {/* Customer type */}
            <Field
              as={Select}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.customerType)}
              name='customerType'
              options={resolveSelectOptions('customerType', 'blocks:staticForms.customerTypeOptionsSupport')}
              required
            />
            {/* END | Customer type */}

            {/* Concern type */}
            <Field
              as={Select}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.issueType)}
              name='issueType'
              options={resolveSelectOptions('issueType', 'blocks:staticForms.issueTypeOptionsSupport')}
              required
            />
            {/* END | Concern type */}
          </FormSection>

          <FormSection numberOfColumns={{ [Breakpoint.Sm]: 2 }}>
            {/* Site name */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.siteName)}
              name='siteName'
            />
            {/* END | Site name */}

            {/* Impacted chargers */}
            <Field
              as={Input}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.impactedChargers)}
              name='impactedChargers'
            />
            {/* END | Impacted chargers */}
          </FormSection>

          {/* Message (concern description) */}
          <FormSection numberOfColumns={1}>
            <Field
              as={Textarea}
              themeColor={formElementThemeColor}
              label={t(PRODUCT_SUPPORT_FORM_USA_EN_FIELD_TO_LABEL_TRANSLATION_KEY_MAP.message)}
              name='message'
              rows='4'
              required
            />
          </FormSection>
          {/* END | Message (concern description) */}

          {/* Google reCAPTCHA */}
          <StyledStaticForms.CaptchaContainer>
            <ReCAPTCHA
              hl={getCaptchaHlBasedOnLocale(locale)}
              onChange={token => setFieldValue('gRecaptchaResponse', token)}
              sitekey={getenv('NEXT_PUBLIC_RECAPTCHA_SITE_KEY', process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY)}
              size='normal'
            />

            {/* CAPTCHA error message */}
            {touched.gRecaptchaResponse && errors.gRecaptchaResponse && (
              <StyledFormElements.ErrorMessage>{t('global:errorMissingCaptcha')}</StyledFormElements.ErrorMessage>
            )}
          </StyledStaticForms.CaptchaContainer>
          {/* END | Google reCAPTCHA */}

          {/* "Submit" button */}
          {!isSuccess && (
            <Button
              type='submit'
              themeColor={buttonThemeColor}
              hoverThemeColor={buttonHoverThemeColor}
              isLoading={isSubmitting}
              disabled={isSuccess || Object.keys(touched).length === 0 || Object.keys(errors).length > 0}
              minWidth>
              {t('global:send')}
            </Button>
          )}
          {/* END | "Submit" button */}

          {/* Success message/feedback */}
          {isSuccess && <SuccessMessage marginTop>{t('global:contact.thanksMessage')}</SuccessMessage>}

          {/* Error message/feedback */}
          {isError && <ErrorMessage marginTop>{t('global:errorUnknown')}</ErrorMessage>}
        </Form>
      )}
    </Formik>
  );
};

export default ProductSupportFormUSAEN;
